import { CommonModule } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, InjectionToken, Signal, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountGroupService as AccountGroupSDK, ProjectionFilter } from '@galaxy/account-group';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { GalaxyPopoverModule } from '@vendasta/galaxy/popover';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { BusinessCommunicationsApiService } from '@vendasta/google-my-business';
import { Observable, firstValueFrom, lastValueFrom, map, shareReplay, switchMap } from 'rxjs';
import { ACCOUNT_GROUP_ID_TOKEN, PARTNER_ID_TOKEN } from '../../../../core/src/lib/tokens';
import { SocialConnectionLinks, SocialPageMeta } from '../interfaces';
import { GoogleBusinessConnectionsService } from './google-business-connections.service';
import { isNativeApp } from '../../../../core/src/lib/mobile';
import { ManageOnWebComponent } from '../manage-on-web/manage-on-web.component';

// gbp = Google Business Profile
type alertTypes = 'no-alert' | 'auth-insufficient' | 'gbp-not-connected' | 'gbp-not-verified' | 'setup-blocked';

const googleBusinessConnectionsServiceToken = new InjectionToken<GoogleBusinessConnectionsService>(
  'GoogleBusinessConnectionsService',
);

function googleBusinessConnectionsServiceFactory(): GoogleBusinessConnectionsService {
  try {
    return inject(GoogleBusinessConnectionsService);
  } catch (e) {
    return new GoogleBusinessConnectionsService(inject(HttpClient));
  }
}

@Component({
  standalone: true,
  selector: 'inbox-google-business-communication-card',
  templateUrl: './google-business-communication-card.component.html',
  styleUrls: ['./google-business-communication-card.component.scss'],
  imports: [
    CommonModule,
    GalaxyBadgeModule,
    MatButtonModule,
    MatCardModule,
    MatBadgeModule,
    FormsModule,
    GalaxyButtonLoadingIndicatorModule,
    MatProgressSpinnerModule,
    GalaxyAlertModule,
    MatIconModule,
    GalaxyTooltipModule,
    GalaxyBadgeModule,
    GalaxyPopoverModule,
    TranslateModule,
    ManageOnWebComponent,
  ],
  providers: [
    {
      provide: googleBusinessConnectionsServiceToken,
      useFactory: googleBusinessConnectionsServiceFactory,
    },
  ],
})
export class GoogleBusinessCommunicationCardComponent {
  private loadingEnabledStatus = signal(true);
  private loadingCanEnable = signal(true);

  private readonly socialProfileId$: Observable<string>;

  // this means the feature is active
  readonly googleBusinessMessagesEnabled = signal(false);
  readonly canEnable = signal(false);
  readonly setupBlocked = signal(false);
  readonly isNativeApp = isNativeApp();

  readonly loading: Signal<boolean>;
  showAlert: Signal<alertTypes>;
  badgeColor: Signal<'red' | 'green'>;

  readonly businessInfo = signal<SocialPageMeta | null>(null);
  private readonly loadingGoogleBusinessConnectionStatus = signal(true);
  private readonly googleBusinessProfileConnected = computed(() => this.businessInfo() !== null);
  private readonly googleBusinessProfileVerified = computed(() => this.businessInfo()?.isVerified ?? false);

  cardDescription: Signal<string>;

  actionErrors = signal<{ title: string; description: string } | null>(null);

  private serviceName = 'googleplus';

  constructor(
    @Inject(PARTNER_ID_TOKEN) private readonly partnerId$: Observable<string>,
    @Inject(ACCOUNT_GROUP_ID_TOKEN) readonly accountGroupId$: Observable<string>,
    private readonly googleMyBusinessService: BusinessCommunicationsApiService,
    private readonly accountGroupSDK: AccountGroupSDK,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    @Inject(googleBusinessConnectionsServiceToken)
    private readonly gBizConnectionsService: GoogleBusinessConnectionsService,
  ) {
    this.socialProfileId$ = this.accountGroupId$.pipe(
      switchMap((agid) => {
        const pf = new ProjectionFilter({
          accountGroupExternalIdentifiers: true,
        });
        return this.accountGroupSDK.get(agid, pf).pipe(map((ag) => ag.externalIdentifiers.socialProfileId));
      }),
      shareReplay(1),
    );

    this.initGoogleBusinessMessagesEnabledStatus();
    this.initCanEnableBusinessCommunications();
    this.initGoogleBusinessProfileConnectionStatus();

    this.loading = computed(
      () => this.loadingEnabledStatus() || this.loadingCanEnable() || this.loadingGoogleBusinessConnectionStatus(),
    );

    this.showAlert = computed(() => {
      if (this.loading()) return 'no-alert';
      // User attempted to connect and an un-retryable error occurred
      if (this.setupBlocked()) return 'setup-blocked';
      if (!this.googleBusinessProfileConnected()) return 'gbp-not-connected';
      if (this.googleBusinessProfileConnected() && !this.googleBusinessProfileVerified()) return 'gbp-not-verified';
      // Checks if the Google Business Profile access token is valid and has the required scopes
      if (this.googleBusinessProfileConnected() && !this.canEnable()) return 'auth-insufficient';
      return 'no-alert';
    });

    this.badgeColor = computed(() => {
      return this.googleBusinessMessagesEnabled() ? 'green' : 'red';
    });

    this.cardDescription = computed(() => {
      if (!this.googleBusinessMessagesEnabled() && !this.loading()) {
        return 'INBOX.SETTINGS.GOOGLE_BUSINESS_MESSAGES.CONNECT_DESCRIPTION';
      }
      return '';
    });
  }

  private async initGoogleBusinessMessagesEnabledStatus() {
    this.loadingEnabledStatus.set(true);
    const partnerId = await firstValueFrom(this.partnerId$);
    const accountGroupId = await firstValueFrom(this.accountGroupId$);

    try {
      const response = await lastValueFrom(
        this.googleMyBusinessService.getBusinessLaunchStatus({ partnerId: partnerId, accountGroupId: accountGroupId }),
      );
      this.googleBusinessMessagesEnabled.set(response.isLaunched ?? false);
    } catch {
      this.googleBusinessMessagesEnabled.set(false);
    } finally {
      this.loadingEnabledStatus.set(false);
    }
  }

  private async initCanEnableBusinessCommunications() {
    this.loadingCanEnable.set(true);
    const socialProfileId = await firstValueFrom(this.socialProfileId$);

    try {
      const response = await firstValueFrom(
        this.googleMyBusinessService.canEnableBusinessCommunications({ socialProfileId }),
      );
      this.canEnable.set(response.hasRequiredAccessScopes ?? false);
    } catch (e) {
      this.canEnable.set(false);
    } finally {
      this.loadingCanEnable.set(false);
    }
  }

  private async initGoogleBusinessProfileConnectionStatus() {
    this.loadingGoogleBusinessConnectionStatus.set(true);
    const accountGroupId = await firstValueFrom(this.accountGroupId$);

    try {
      const response = await lastValueFrom(this.gBizConnectionsService.getSocialProfileConnections(accountGroupId));
      this.businessInfo.set(response.google_my_business[0] ?? null);
    } catch {
      this.businessInfo.set(null);
    } finally {
      this.loadingGoogleBusinessConnectionStatus.set(false);
    }
  }

  async reauthorize(): Promise<void> {
    const accountGroupId = await firstValueFrom(this.accountGroupId$);

    const protocol = window.location.protocol;
    const host = window.location.host;
    const baseUrl = `${protocol}//${host}`;
    const newURL: URL = new URL(baseUrl);
    newURL.pathname = `/account/location/${accountGroupId}/settings/connections`;

    const authUrls = await firstValueFrom(this.gBizConnectionsService.getAuthUrls(accountGroupId, newURL));
    const serviceAuthURL = authUrls[this.serviceName as keyof SocialConnectionLinks];
    const oauthUrl = [
      serviceAuthURL.slice(0, serviceAuthURL.indexOf('&spid')),
      `?type=Google%20My%20Business`,
      serviceAuthURL.slice(serviceAuthURL.indexOf('&spid')),
    ].join('');
    window.location.href = oauthUrl;
  }

  redirectToConnections() {
    // This will only work if the parent app is Business Center.
    this.router.navigate(['../connections'], { relativeTo: this.route });
  }

  async connectGoogleBusinessCommunications() {
    this.loadingEnabledStatus.set(true);
    const accountID = await firstValueFrom(this.accountGroupId$);
    const partnerID = await firstValueFrom(this.partnerId$);
    const socialProfileID = await firstValueFrom(this.socialProfileId$);

    try {
      await firstValueFrom(
        this.googleMyBusinessService.connectBusinessCommunications({
          partnerId: partnerID,
          accountGroupId: accountID,
          socialProfileId: socialProfileID,
        }),
      );
      this.googleBusinessMessagesEnabled.set(true);
    } catch (e) {
      if (
        e instanceof HttpErrorResponse &&
        typeof e.error.message == 'string' &&
        e.error.message.includes('failed to launch agent due to enabled locations with same place-ids')
      ) {
        this.actionErrors.set({
          title: 'INBOX.SETTINGS.GOOGLE_BUSINESS_MESSAGES.ERROR_CONNECTING',
          description: 'INBOX.SETTINGS.GOOGLE_BUSINESS_MESSAGES.FAILED_TO_TURN_ON_ALREADY_CONNECTED_TO_OTHER_APP',
        });
        this.setupBlocked.set(true);
      } else {
        this.actionErrors.set({
          title: 'INBOX.SETTINGS.GOOGLE_BUSINESS_MESSAGES.ERROR_CONNECTING',
          description: 'INBOX.SETTINGS.GOOGLE_BUSINESS_MESSAGES.FAILED_TO_TURN_ON',
        });
      }
    } finally {
      this.loadingEnabledStatus.set(false);
    }
  }

  async unlaunchGoogleBusinessCommunications() {
    this.loadingEnabledStatus.set(true);

    const accountID = await firstValueFrom(this.accountGroupId$);
    const partnerID = await firstValueFrom(this.partnerId$);

    try {
      await firstValueFrom(
        this.googleMyBusinessService.unlaunchBusinessCommunicationsAgent({
          partnerId: partnerID,
          accountGroupId: accountID,
        }),
      );
      this.googleBusinessMessagesEnabled.set(false);
    } catch (e) {
      this.actionErrors.set({
        title: 'INBOX.SETTINGS.GOOGLE_BUSINESS_MESSAGES.ERROR_DISCONNECTING',
        description: 'INBOX.SETTINGS.GOOGLE_BUSINESS_MESSAGES.FAILED_TO_TURN_OFF',
      });
    } finally {
      this.loadingEnabledStatus.set(false);
    }
  }
}
